import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { Swords, Users, Search, RefreshCw, X, Zap, Trophy } from 'lucide-react';

// ── Sport emoji ──────────────────────────────────────────────────
const getSportEmoji = (category = '') => {
  const c = (category || '').toLowerCase();
  if (c.includes('soccer') || (c.includes('football') && (c.includes('eu') || c.includes('world')))) return '⚽';
  if (c.includes('basketball') || c.includes('nba')) return '🏀';
  if (c.includes('baseball') || c.includes('mlb') || c.includes('softball')) return '⚾';
  if (c.includes('hockey') || c.includes('nhl')) return '🏒';
  if (c.includes('tennis')) return '🎾';
  if (c.includes('volleyball')) return '🏐';
  if (c.includes('rugby')) return '🏉';
  if (c.includes('golf')) return '⛳';
  if (c.includes('football') || c.includes('nfl') || c.includes('college')) return '🏈';
  return '⚔️';
};

// ── Credits (localStorage) ───────────────────────────────────────
const CREDIT_MAX = 100;
const COOLDOWN_SECS = 60;
const CREDIT_KEY = 'rc_credits_v1';
const COOLDOWN_END_KEY = 'rc_cooldown_end_v1';

function loadCredits() {
  try {
    const stored = localStorage.getItem(CREDIT_KEY);
    const c = stored ? parseInt(stored, 10) : CREDIT_MAX;
    return Number.isFinite(c) && c >= 0 ? c : CREDIT_MAX;
  } catch { return CREDIT_MAX; }
}
function saveCredits(n) {
  try { localStorage.setItem(CREDIT_KEY, String(n)); } catch {}
}
function loadCooldownEnd() {
  try {
    const v = localStorage.getItem(COOLDOWN_END_KEY);
    return v ? parseInt(v, 10) : null;
  } catch { return null; }
}
function saveCooldownEnd(ts) {
  try {
    if (ts) localStorage.setItem(COOLDOWN_END_KEY, String(ts));
    else localStorage.removeItem(COOLDOWN_END_KEY);
  } catch {}
}

// ── Helpers ──────────────────────────────────────────────────────
const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const STATUS_COLORS = {
  live:      { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.4)',  text: '#EF4444',  dot: '#EF4444',  label: 'LIVE' },
  ongoing:   { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.4)',  text: '#EF4444',  dot: '#EF4444',  label: 'LIVE' },
  started:   { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.4)',  text: '#EF4444',  dot: '#EF4444',  label: 'LIVE' },
  upcoming:  { bg: 'rgba(14,165,233,0.1)', border: 'rgba(14,165,233,0.3)', text: '#38BDF8',  dot: null,       label: 'UPCOMING' },
  scheduled: { bg: 'rgba(14,165,233,0.1)', border: 'rgba(14,165,233,0.3)', text: '#38BDF8',  dot: null,       label: 'UPCOMING' },
  completed: { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.3)', text: '#94A3B8', dot: null,      label: 'ENDED' },
  ended:     { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.3)', text: '#94A3B8', dot: null,      label: 'ENDED' },
  finished:  { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.3)', text: '#94A3B8', dot: null,      label: 'ENDED' },
};

const getStatusInfo = (challenge) => {
  const s = String(challenge.status || '').toLowerCase();
  return STATUS_COLORS[s] || STATUS_COLORS.upcoming;
};

const isLiveFn = (c) => {
  const s = String(c.status || '').toLowerCase();
  return s === 'live' || s === 'ongoing' || s === 'started' || c.is_live === true;
};
const isEndedFn = (c) => {
  const s = String(c.status || '').toLowerCase();
  return s === 'ended' || s === 'completed' || s === 'finished' || s === 'done';
};

// ── Pulsing dot ──────────────────────────────────────────────────
function PulsingDot() {
  return <span className="pulsing-dot" />;
}

// ── Challenge card ───────────────────────────────────────────────
function ChallengeCard({ challenge, requestCounts, voteCounts, requestingIds, onRequest, onEnter, onDemandRematch, credits, cooldownSecs }) {
  const statusInfo = getStatusInfo(challenge);
  const isLive = isLiveFn(challenge);
  const isEnded = isEndedFn(challenge);
  const sportEmoji = getSportEmoji(challenge.category_name || challenge.category);
  const colorA = challenge.teamA_color || '#0EA5E9';
  const colorB = challenge.teamB_color || '#7DD3FC';

  const liveVotes = voteCounts[challenge.id];
  const teamALikes = liveVotes?.teamA_likes ?? challenge.teamA_likes ?? 0;
  const teamBLikes = liveVotes?.teamB_likes ?? challenge.teamB_likes ?? 0;
  const total = teamALikes + teamBLikes;
  const pA = total ? Math.round((teamALikes / total) * 100) : 50;
  const pB = 100 - pA;

  const rawCount = requestCounts[challenge.id] ?? challenge.requests_needed ?? challenge.requests_remaining;
  const count = rawCount != null && Number.isFinite(Number(rawCount)) ? Number(rawCount) : 0;
  const fillPct = Math.max(4, Math.min(100, 100 - Math.min(count * 10, 90)));

  const stripeColors = isEnded
    ? 'linear-gradient(90deg,#475569,#64748B,#94A3B8)'
    : isLive
      ? 'linear-gradient(90deg,#F97316,#EF4444,#DC2626)'
      : 'linear-gradient(90deg,#0EA5E9,#38BDF8,#BAE6FD)';

  const isRequesting = requestingIds[challenge.id];

  const handleBtn = () => {
    if (isEnded) { onDemandRematch(challenge); return; }
    if (isLive)  { onEnter(challenge); return; }
    onRequest(challenge);
  };

  return (
    <div className="ch-card">
      <div className="ch-stripe" style={{ background: stripeColors }} />
      <div className="ch-bg-grad" style={{
        background: `linear-gradient(135deg, ${colorA}14 0%, #1A2234 50%, ${colorB}14 100%)`
      }} />

      {/* Top row */}
      <div className="ch-top-row">
        <span className="ch-category">{(challenge.category_name || challenge.category || 'RIVALRY').toUpperCase()}</span>
        <div className="ch-top-right">
          {challenge.is_rematch && <span className="ch-pill ch-pill--rematch">⚔️ REMATCH</span>}
          {isEnded ? (
            <span className="ch-pill ch-pill--ended">ENDED</span>
          ) : isLive ? (
            <span className="ch-pill ch-pill--live"><PulsingDot />LIVE</span>
          ) : (
            <span className="ch-pill ch-pill--upcoming">UPCOMING</span>
          )}
        </div>
      </div>

      {/* Arena */}
      <div className="ch-arena">
        <div className="ch-team-side">
          <div className="ch-logo-ring" style={{ borderColor: `${colorA}60`, boxShadow: `0 0 18px ${colorA}44` }}>
            <div className="ch-logo-grad" style={{ background: `linear-gradient(135deg, ${colorA}44, ${colorA}18)` }}>
              <span className="ch-sport-emoji">{sportEmoji}</span>
            </div>
          </div>
          <span className="ch-team-name">{challenge.teamA_name || 'Team A'}</span>
          <span className="ch-team-votes" style={{ color: colorA }}>{fmtNum(teamALikes)}</span>
        </div>

        <div className="ch-vs-divider">
          <div className="ch-vs-line" style={{ background: `${colorA}40` }} />
          <div className="ch-vs-circle">
            <Swords size={14} color="#fff" />
            <span className="ch-vs-word">VS</span>
          </div>
          <div className="ch-vs-line" style={{ background: `${colorB}40` }} />
        </div>

        <div className="ch-team-side">
          <div className="ch-logo-ring" style={{ borderColor: `${colorB}60`, boxShadow: `0 0 18px ${colorB}44` }}>
            <div className="ch-logo-grad" style={{ background: `linear-gradient(135deg, ${colorB}44, ${colorB}18)` }}>
              <span className="ch-sport-emoji" style={{ transform: 'scaleX(-1)', display: 'inline-block' }}>{sportEmoji}</span>
            </div>
          </div>
          <span className="ch-team-name">{challenge.teamB_name || 'Team B'}</span>
          <span className="ch-team-votes" style={{ color: colorB }}>{fmtNum(teamBLikes)}</span>
        </div>
      </div>

      {/* Split bar */}
      <div className="ch-split-wrap">
        <div className="ch-split-bar">
          <div className="ch-split-a" style={{ flex: pA, background: colorA }} />
          <div className="ch-split-b" style={{ flex: pB, background: colorB }} />
        </div>
        <div className="ch-split-labels">
          <span style={{ color: colorA, fontWeight: 900, fontSize: 12 }}>{pA}%</span>
          <span className="ch-total-votes"><Users size={11} /> {fmtNum(total)} votes</span>
          <span style={{ color: colorB, fontWeight: 900, fontSize: 12 }}>{pB}%</span>
        </div>
      </div>

      {/* Request progress (upcoming only) */}
      {!isLive && !isEnded && (
        <div className="ch-request-progress">
          <div className="ch-request-track">
            <div className="ch-request-fill" style={{ width: `${fillPct}%` }} />
          </div>
          <span className="ch-request-label">
            {count > 0 ? `${count} more requests needed to launch` : 'Ready to launch!'}
          </span>
        </div>
      )}

      {isEnded && (
        <p className="ch-ended-hint">⚔️ Recruit your squad — demand a rematch!</p>
      )}

      {/* Action button */}
      {isEnded ? (
        <button className="ch-btn ch-btn--ended" onClick={handleBtn}>
          🔄 Demand Another Round
        </button>
      ) : isLive ? (
        <button className="ch-btn ch-btn--live" onClick={handleBtn} disabled={isRequesting}>
          <PulsingDot /> {isRequesting ? 'Entering…' : 'Enter Challenge'}
        </button>
      ) : (
        <button
          className={`ch-btn ch-btn--request${cooldownSecs > 0 ? ' ch-btn--cooldown' : ''}`}
          onClick={handleBtn}
          disabled={isRequesting}
        >
          {isRequesting ? 'Requesting…' : cooldownSecs > 0 ? `⏳ Refills in ${cooldownSecs}s` : '🔥 Request to Start'}
          {!isRequesting && cooldownSecs <= 0 && <span className="ch-btn-sub">· make it happen</span>}
        </button>
      )}
    </div>
  );
}

// ── Vote modal (for live challenges) ────────────────────────────
function VoteModal({ challenge, voteCounts, token, onClose }) {
  const [votedTeam, setVotedTeam] = useState(null);
  const [voting, setVoting] = useState(false);
  const [wsVotes, setWsVotes] = useState(null);
  const [error, setError] = useState('');

  const colorA = challenge.teamA_color || '#0EA5E9';
  const colorB = challenge.teamB_color || '#7DD3FC';
  const liveVotes = voteCounts[challenge.id];
  const teamALikes = wsVotes?.teamA_likes ?? liveVotes?.teamA_likes ?? challenge.teamA_likes ?? 0;
  const teamBLikes = wsVotes?.teamB_likes ?? liveVotes?.teamB_likes ?? challenge.teamB_likes ?? 0;
  const total = teamALikes + teamBLikes;
  const pA = total ? Math.round((teamALikes / total) * 100) : 50;
  const pB = 100 - pA;
  const sportEmoji = getSportEmoji(challenge.category_name);

  useEffect(() => {
    const wsUrl = token
      ? `wss://chat.meanc.net/ws/challenge/?token=${token}`
      : 'wss://chat.meanc.net/ws/challenge/';
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'challenge_update' && msg.challenge?.id === challenge.id) {
            const c = msg.challenge;
            setWsVotes(prev => ({
              teamA_likes: c.teamA_likes ?? prev?.teamA_likes,
              teamB_likes: c.teamB_likes ?? prev?.teamB_likes,
            }));
          }
        } catch {}
      };
    } catch {}
    return () => ws?.close();
  }, [challenge.id, token]);

  const handleVote = async (teamId, side) => {
    if (!token) { setError('Sign in to vote.'); return; }
    if (votedTeam && votedTeam !== side) { setError('You already voted for the other team.'); return; }
    setVoting(true); setError('');
    try {
      const data = await api.vote(token, challenge.id, teamId, 1);
      const v = data?.payload ?? data;
      if (typeof v?.team_a_votes === 'number' || typeof v?.teamA_likes === 'number') {
        setWsVotes({
          teamA_likes: v.team_a_votes ?? v.teamA_likes ?? teamALikes,
          teamB_likes: v.team_b_votes ?? v.teamB_likes ?? teamBLikes,
        });
      }
      setVotedTeam(side);
    } catch { setError('Vote failed. Try again.'); }
    finally { setVoting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vote-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}><X size={18} /></button>

        <div className="vm-header" style={{ background: `linear-gradient(135deg, ${colorA}22, ${colorB}22)` }}>
          <span className="vm-category">{(challenge.category_name || 'RIVALRY').toUpperCase()}</span>
          <span className="vm-pill-live"><PulsingDot /> LIVE BATTLE</span>
        </div>

        <div className="vm-arena">
          <div className="vm-team">
            <div className="vm-logo" style={{ borderColor: `${colorA}60`, boxShadow: `0 0 24px ${colorA}44` }}>
              <div style={{ background: `linear-gradient(135deg, ${colorA}44, ${colorA}18)`, width: '100%', height: '100%', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>
                {sportEmoji}
              </div>
            </div>
            <span className="vm-team-name">{challenge.teamA_name || 'Team A'}</span>
            <span className="vm-pct" style={{ color: colorA }}>{pA}%</span>
            <span className="vm-votes">{fmtNum(teamALikes)} votes</span>
            <button
              className="vm-vote-btn"
              style={{ background: `linear-gradient(135deg, ${colorA}, ${colorA}99)`, opacity: votedTeam === 'b' ? 0.35 : 1 }}
              disabled={voting || votedTeam === 'b'}
              onClick={() => handleVote(challenge.teamA, 'a')}
            >
              {votedTeam === 'a' ? '✓ Voted!' : '⚡ Vote'}
            </button>
          </div>

          <div className="vm-vs">
            <Swords size={28} color="#0EA5E9" />
            <span className="vm-vs-text">VS</span>
            <span className="vm-total">{fmtNum(total)} total</span>
          </div>

          <div className="vm-team">
            <div className="vm-logo" style={{ borderColor: `${colorB}60`, boxShadow: `0 0 24px ${colorB}44` }}>
              <div style={{ background: `linear-gradient(135deg, ${colorB}44, ${colorB}18)`, width: '100%', height: '100%', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, transform: 'scaleX(-1)' }}>
                {sportEmoji}
              </div>
            </div>
            <span className="vm-team-name">{challenge.teamB_name || 'Team B'}</span>
            <span className="vm-pct" style={{ color: colorB }}>{pB}%</span>
            <span className="vm-votes">{fmtNum(teamBLikes)} votes</span>
            <button
              className="vm-vote-btn"
              style={{ background: `linear-gradient(135deg, ${colorB}, ${colorB}99)`, opacity: votedTeam === 'a' ? 0.35 : 1 }}
              disabled={voting || votedTeam === 'a'}
              onClick={() => handleVote(challenge.teamB, 'b')}
            >
              {votedTeam === 'b' ? '✓ Voted!' : '⚡ Vote'}
            </button>
          </div>
        </div>

        <div className="vm-split">
          <div className="vm-split-bar">
            <div style={{ flex: pA, background: colorA, height: '100%', transition: 'flex 0.6s ease', borderRadius: '4px 0 0 4px' }} />
            <div style={{ flex: pB, background: colorB, height: '100%', transition: 'flex 0.6s ease', borderRadius: '0 4px 4px 0' }} />
          </div>
        </div>

        {error && <p className="vm-error">{error}</p>}
      </div>
    </div>
  );
}

// ── Cooldown modal ───────────────────────────────────────────────
function CooldownModal({ cooldownSecs, onClose }) {
  const pct = Math.max(0, Math.min(100, ((COOLDOWN_SECS - cooldownSecs) / COOLDOWN_SECS) * 100));
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cd-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}><X size={18} /></button>
        <div className="cd-badge" style={{ background: cooldownSecs > 0 ? '#F97316' : '#0EA5E9' }}>
          {cooldownSecs > 0 ? <span style={{ color: '#fff', fontWeight: 900, fontSize: 18 }}>{cooldownSecs}</span> : <Zap size={22} color="#fff" />}
        </div>
        <h3 className="cd-title">{cooldownSecs > 0 ? 'Credits refilling…' : 'Credits ready!'}</h3>
        {cooldownSecs > 0 ? (
          <>
            <p className="cd-text">Your 100 credits are recharging. Back in <strong style={{ color: '#F97316' }}>{cooldownSecs}s</strong>.</p>
            <div className="cd-track"><div className="cd-fill" style={{ width: `${pct}%` }} /></div>
          </>
        ) : (
          <p className="cd-text">Your 100 credits are ready. Get back in the rivalry!</p>
        )}
        <button className="cd-btn" onClick={onClose}>{cooldownSecs > 0 ? 'Wait in background' : "Let's go! ⚔️"}</button>
      </div>
    </div>
  );
}

// ── Demand rematch modal ─────────────────────────────────────────
function DemandModal({ challenge, onRequest, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cd-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}><X size={18} /></button>
        <span style={{ fontSize: 42, marginBottom: 4 }}>🔄</span>
        <h3 className="cd-title">Demand Another Round</h3>
        <p className="cd-text">
          <strong>{challenge.teamA_name}</strong> vs <strong>{challenge.teamB_name}</strong> ended.
          Recruit your squad and request a rematch!
        </p>
        <button className="cd-btn" onClick={() => { onRequest(challenge); onClose(); }}>⚔️ Request a Rematch</button>
        <button className="cd-btn cd-btn--ghost" onClick={onClose}>Maybe later</button>
      </div>
    </div>
  );
}

// ── Hero stats bar ───────────────────────────────────────────────
function StatsBar({ challenges, credits, cooldownSecs }) {
  const liveCount = useMemo(() => challenges.filter(isLiveFn).length, [challenges]);
  return (
    <div className="stats-bar">
      <div className="stats-bar-item">
        <span className="stats-live-dot" />
        <span className="stats-num">{liveCount}</span>
        <span className="stats-label">Live Now</span>
      </div>
      <div className="stats-bar-divider" />
      <div className="stats-bar-item">
        <span className="stats-num">{challenges.length}</span>
        <span className="stats-label">Total Battles</span>
      </div>
      <div className="stats-bar-divider" />
      <div className="stats-bar-item">
        {cooldownSecs > 0 ? (
          <>
            <span className="stats-num" style={{ color: '#F97316' }}>{cooldownSecs}s</span>
            <span className="stats-label" style={{ color: '#F97316' }}>Refilling</span>
          </>
        ) : (
          <>
            <span className="stats-num" style={{ color: credits <= 20 ? '#EF4444' : '#22C55E' }}>{credits}</span>
            <span className="stats-label">Credits</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────
const FILTERS = ['All', 'Live', 'Upcoming', 'Ended'];

export default function FeedPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [refreshing, setRefreshing] = useState(false);

  // Real-time state
  const [requestCounts, setRequestCounts] = useState({});
  const [voteCounts, setVoteCounts] = useState({});
  const [requestingIds, setRequestingIds] = useState({});
  const wsRef = useRef(null);

  // Credits
  const [credits, setCredits] = useState(() => loadCredits());
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const cooldownRef = useRef(null);

  // Modals
  const [voteChallenge, setVoteChallenge] = useState(null);
  const [demandChallenge, setDemandChallenge] = useState(null);
  const [showCooldown, setShowCooldown] = useState(false);

  // ── Load stored cooldown on mount ──────────────────────────────
  useEffect(() => {
    const cooldownEnd = loadCooldownEnd();
    if (cooldownEnd && cooldownEnd > Date.now()) {
      const secs = Math.ceil((cooldownEnd - Date.now()) / 1000);
      setCooldownSecs(secs);
      startCountdown(cooldownEnd);
    } else if (cooldownEnd) {
      resetCredits();
    }
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const startCountdown = useCallback((endTs) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    const tick = () => {
      const left = Math.ceil((endTs - Date.now()) / 1000);
      if (left <= 0) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
        resetCredits();
      } else {
        setCooldownSecs(left);
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
  }, []);

  const resetCredits = () => {
    setCredits(CREDIT_MAX);
    setCooldownSecs(0);
    setShowCooldown(false);
    saveCredits(CREDIT_MAX);
    saveCooldownEnd(null);
  };

  const deductCredit = useCallback(() => {
    let newCredits;
    setCredits(prev => {
      newCredits = Math.max(0, prev - 1);
      saveCredits(newCredits);
      return newCredits;
    });
    setTimeout(() => {
      if (newCredits <= 0) {
        const endTs = Date.now() + COOLDOWN_SECS * 1000;
        saveCooldownEnd(endTs);
        setCooldownSecs(COOLDOWN_SECS);
        setShowCooldown(true);
        startCountdown(endTs);
      }
    }, 0);
    return newCredits ?? credits - 1;
  }, [credits, startCountdown]);

  // ── WebSocket ─────────────────────────────────────────────────
  useEffect(() => {
    let retryDelay = 2000;
    let retryTimer = null;

    const connect = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      try {
        const ws = new WebSocket('wss://chat.meanc.net/ws/challenge/');
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type !== 'challenge_update' || !msg.challenge) return;
            const { id, requests_needed, teamA_likes, teamB_likes } = msg.challenge;
            if (id != null && requests_needed != null) {
              setRequestCounts(prev => ({ ...prev, [id]: Number(requests_needed) }));
            }
            if (id != null && (teamA_likes != null || teamB_likes != null)) {
              setVoteCounts(prev => ({
                ...prev,
                [id]: {
                  teamA_likes: teamA_likes ?? prev[id]?.teamA_likes,
                  teamB_likes: teamB_likes ?? prev[id]?.teamB_likes,
                },
              }));
            }
          } catch {}
        };
        ws.onopen = () => { retryDelay = 2000; };
        ws.onerror = () => ws.close();
        ws.onclose = () => {
          wsRef.current = null;
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 60000);
        };
      } catch {}
    };

    connect();
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      wsRef.current?.close();
    };
  }, []);

  // ── Fetch challenges ──────────────────────────────────────────
  const fetchChallenges = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await api.getChallenges(token);
      const list = Array.isArray(data) ? data : data?.results ?? [];
      setChallenges(list);
      // Seed request counts from API
      const counts = {};
      list.forEach(c => {
        const v = c.requests_needed ?? c.requests_remaining;
        if (v != null) counts[c.id] = Number(v);
      });
      setRequestCounts(prev => ({ ...counts, ...prev }));
    } catch {
      setError('Could not load rivalries. Check your connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);
  // Auto-refresh every 60s
  useEffect(() => {
    const t = setInterval(() => fetchChallenges(true), 60000);
    return () => clearInterval(t);
  }, [fetchChallenges]);

  // ── Request to start ─────────────────────────────────────────
  const handleRequest = useCallback(async (challenge) => {
    if (cooldownSecs > 0) { setShowCooldown(true); return; }
    if (requestingIds[challenge.id]) return;

    const newCredits = deductCredit();
    if (newCredits <= 0) return;

    setRequestingIds(prev => ({ ...prev, [challenge.id]: true }));
    try {
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch('https://chat.meanc.net/chat-api/v2/challenges/request/', {
        method: 'POST',
        headers,
        body: JSON.stringify({ challenge_id: challenge.id }),
      });
      const payload = await res.json().catch(() => ({}));

      if (res.status === 429 || payload?.allowed === false) {
        setShowCooldown(true);
        return;
      }

      const serverCount = payload?.requests_needed ?? payload?.requests_remaining ?? payload?.requestsNeeded;
      if (serverCount != null) {
        setRequestCounts(prev => ({ ...prev, [challenge.id]: Number(serverCount) }));
      } else {
        setRequestCounts(prev => ({
          ...prev,
          [challenge.id]: Math.max(0, (prev[challenge.id] ?? 0) - 1),
        }));
      }
      fetchChallenges(true);
    } catch {
      // silently fail
    } finally {
      setRequestingIds(prev => ({ ...prev, [challenge.id]: false }));
    }
  }, [cooldownSecs, requestingIds, token, deductCredit, fetchChallenges]);

  // ── Filter ────────────────────────────────────────────────────
  const filtered = useMemo(() => challenges.filter(c => {
    const s = String(c.status || '').toLowerCase();
    const name = `${c.teamA_name || ''} ${c.teamB_name || ''} ${c.category_name || c.category || ''}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filter === 'Live') return isLiveFn(c);
    if (filter === 'Upcoming') return !isLiveFn(c) && !isEndedFn(c);
    if (filter === 'Ended') return isEndedFn(c);
    return true;
  }), [challenges, search, filter]);

  const liveCount = useMemo(() => challenges.filter(isLiveFn).length, [challenges]);

  return (
    <div className="feed-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <Swords size={24} className="page-title-icon" />
            Rivalries
          </h1>
          {liveCount > 0 && (
            <div className="live-count-badge">
              <span className="live-dot" />
              {liveCount} Live
            </div>
          )}
        </div>
        <button className="icon-btn" onClick={() => { setRefreshing(true); fetchChallenges(true); }}
          disabled={refreshing} title="Refresh">
          <RefreshCw size={17} className={refreshing ? 'spin' : ''} />
        </button>
      </div>

      {/* Stats bar */}
      <StatsBar challenges={challenges} credits={credits} cooldownSecs={cooldownSecs} />

      {/* Search + Filters */}
      <div className="feed-controls">
        <div className="search-wrap">
          <Search size={15} color="#64748B" className="search-icon" />
          <input className="search-input" placeholder="Search rivalries, teams…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="filter-chips">
          {FILTERS.map(f => (
            <button key={f} className={`filter-chip ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="ch-skeleton-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="ch-skeleton">
              <div className="sk-stripe" />
              <div className="sk-arena">
                <div className="sk-circle" />
                <div className="sk-vs" />
                <div className="sk-circle" />
              </div>
              <div className="sk-bar" />
              <div className="sk-btn" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="feed-error">
          <Swords size={40} color="rgba(239,68,68,0.4)" />
          <p>{error}</p>
          <button className="auth-btn" onClick={() => fetchChallenges()} style={{ width: 'auto', padding: '10px 24px' }}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="feed-empty">
          <Swords size={48} color="rgba(14,165,233,0.3)" />
          <p>No rivalries found</p>
          <span>Try a different filter or check back later.</span>
        </div>
      ) : (
        <div className="ch-grid">
          {filtered.map(c => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              requestCounts={requestCounts}
              voteCounts={voteCounts}
              requestingIds={requestingIds}
              onRequest={handleRequest}
              onEnter={setVoteChallenge}
              onDemandRematch={setDemandChallenge}
              credits={credits}
              cooldownSecs={cooldownSecs}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {voteChallenge && (
        <VoteModal
          challenge={voteChallenge}
          voteCounts={voteCounts}
          token={token}
          onClose={() => setVoteChallenge(null)}
        />
      )}
      {showCooldown && (
        <CooldownModal cooldownSecs={cooldownSecs} onClose={() => setShowCooldown(false)} />
      )}
      {demandChallenge && (
        <DemandModal
          challenge={demandChallenge}
          onRequest={handleRequest}
          onClose={() => setDemandChallenge(null)}
        />
      )}
    </div>
  );
}
